<template>
  <!-- eslint-disable max-len  -->

  <div class="reg-container">
    <div class="reg-panel content-panel">
      <div v-if="serverInfo">
        <div v-if="registrationDisabled">
          {{ this.$t(LABEL_REGISTRATION_DISABLED) }}
        </div>
        <div v-else-if="displayTrialExpired">
          <div v-if="!isThunderbird">
            {{ $t(LABEL_THUNDERBIRD_SIGNUP) }}
            <div style="margin-top: 0.5rem">
              <a :href="'/' + this.localeUrl + '/thunderbird'">{{ $t(LABEL_LEARN_MORE) }}</a>
            </div>
          </div>

          <div v-else v-html="$t(LABEL_THUNDERBIRD_TOKEN_EXPIRED)"></div>
        </div>
        <div v-else-if="displayRejected">
          <!--  {{ this.$t(LABEL_REGISTRATION_REFUSED) }} -->
          <div style="margin-top: 0.5rem; text-align: left" v-html="contactSupportMessage"></div>
        </div>
        <div v-else-if="thunderbirdNoSupported">
          <div v-html="$t(LABEL_PLEASE_UPGRADE_THUNDERBIRD)"></div>
        </div>

        <div v-else>
          <!-- v-show to have entered data if error -->

          <registration-form
            v-show="displayHome"
            :emailDomains="serverInfo.mailDomains"
            :showBack="info.backBtn && !isThunderbird"
            :lookAndFeel="info.lf"
            :trial="serverInfo.trial"
            :trialDays="serverInfo.trialDays"
            :trialToken="info.token"
            :thunderbirdInfo="isThunderbird"
            :source="info.source"
            :groupInvitation="info.groupInvitation"
            :groupInfo="serverInfo.groupInvitationInfo"
            :serviceName="serviceName"
            :passwordMaxLength="serverInfo.validation.passwordMaxLength"
            :passwordMinLength="serverInfo.validation.passwordMinLength"
            :firstnameMinLength="serverInfo.validation.firstnameMinLength"
            :firstnameMaxLength="serverInfo.validation.firstnameMaxLength"
            :lastnameMinLength="serverInfo.validation.lastnameMinLength"
            :lastnameMaxLength="serverInfo.validation.lastnameMaxLength"
            :usernameMinLength="serverInfo.validation.usernameMinLength"
            :usernameMaxLength="serverInfo.validation.usernameMaxLength"
            :termsUrl="serverInfo.ui.termsUrl"
            :privacyPolicyUrl="serverInfo.ui.privacyPolicyUrl"
            :prefilledParams="info"
            :validationErrors="homeValidationErrors"
            v-on:formSubmitted="onFormSubmitted"
            v-on:onBack="goBack"
          >
          </registration-form>

          <recovery-form
            v-show="displayRecovery"
            :isVisible="displayRecovery"
            :serviceName="serviceName"
            @submitted="onRecoverySubmitted"
            v-on:onBack="goBack"
            :validationErrors="recoveryValidationErrors"
            :processing="isProcessing"
          >
          </recovery-form>

          <captcha-panel
            v-if="displayCaptcha"
            :captchaKey="serverInfo.captchaApiKey"
            :captchaType="serverInfo.captchaType"
            :locale="$i18n.locale"
            @captchaVerified="onCaptchaVerified"
          >
          </captcha-panel>

          <code-validation-form
            v-if="displayCode"
            :email="recoveryEmail"
            :codeLength="serverInfo.codeLength"
            v-on:codeSubmitted="onCodeSubmitted"
            v-on:onBack="goBack"
            v-on:resendCode="resendCode"
            :customerEmail="serverInfo.notificationEmail"
            :validationErrors="codeValidationErrors"
            :processing="isProcessing"
          >
          </code-validation-form>

          <div style="display: flex; justify-content: center"><div class="loader" v-if="displayLoader"></div></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */

import { defineComponent, toRefs, watchEffect } from 'vue';
import {
  LABEL_REGISTRATION_DISABLED,
  LABEL_REGISTRATION_REFUSED,
  LABEL_THUNDERBIRD_TOKEN_EXPIRED,
  LABEL_ERROR_OCCURED,
  LABEL_SIGN_UP,
  LABEL_REJECT_REASON,
  LABEL_PLEASE_UPGRADE_THUNDERBIRD,
  LABEL_THUNDERBIRD_SIGNUP,
  LABEL_LEARN_MORE,
} from '@/libs/i18nLabels';
import RegistrationForm from '../components/registration-form.vue';
import RecoveryForm from '../components/recovery-form.vue';
import CaptchaPanel from '../components/captcha-panel.vue';
import CodeValidationForm from '../components/code-verification.vue';

import thunderbirdParser from '../libs/thunderbird';
import { addQueryStringParams } from '../libs/urlUtils';
import { RegistrationQueryStringInfo } from '../libs/querystringInfo';
import registrationState, { PANEL, StateHandler, FormData } from '../libs/registrationState';

export default defineComponent({
  components: {
    RegistrationForm,
    CodeValidationForm,
    RecoveryForm,
    CaptchaPanel,
  },

  props: {
    userAgent: {
      type: String,
      required: true,
    },
    info: {
      type: Object as () => RegistrationQueryStringInfo,
      required: true,
    },
  },

  setup(props) {
    // Thunderbird check
    const thunderbirdInfo = thunderbirdParser(props.userAgent, '78.8.1');
    if (thunderbirdInfo.isThunderbird) {
      console.log('Thunderbird supported', thunderbirdInfo.isSupported, 'version: ', thunderbirdInfo.version);
    }

    async function onRegistrationCompleted(data: any) {
      const { serverInfo } = registrationState.state;
      // For thunderbird autoconfig
      if (thunderbirdInfo.isSupported) {
        const password = registrationState.formData?.password || '';
        window.location.replace(`/api/v1/thunderbird/config?p=${encodeURIComponent(password)}&t=${Date.now()}`);
        //  to avoid caching issue in thunderbird
      } else {
        // Redirect

        // disable password manager (remove the form from dom and set timeout)
        setTimeout(() => {
          if (serverInfo?.mobileApp === true) {
            window.location.replace(`/?accessToken=${data.accessToken}&refreshToken=${data.refreshToken}`);
            return;
          }

          const infoValue = props.info;
          const redirectUrl = infoValue.nextPage || (serverInfo && serverInfo.ui && serverInfo.ui.redirectAfterRegistration) || '/';
          window.location.replace(redirectUrl);
        }, 10);
      }
    }

    watchEffect(() => {
      // display selected screen
      const panel = registrationState.state.panelToDisplay;
      registrationState.logDisplayedScreen(props.info, panel);
    });

    const { serverInfo } = toRefs(registrationState.state);

    return {
      serverInfo,
      thunderbirdInfo,
      onRegistrationCompleted,
      LABEL_REGISTRATION_DISABLED,
      LABEL_REGISTRATION_REFUSED,
      LABEL_PLEASE_UPGRADE_THUNDERBIRD,
      LABEL_THUNDERBIRD_TOKEN_EXPIRED,
      LABEL_THUNDERBIRD_SIGNUP,
      LABEL_LEARN_MORE,
    };
  },

  methods: {
    onFormSubmitted(data: FormData) {
      registrationState.onFormSubmitted(data);
    },

    onRecoverySubmitted(data: string) {
      registrationState.onRecoveryEmail(data);
    },

    onCaptchaVerified(key: string) {
      registrationState.onCaptchaResolved(key);
    },

    onCodeSubmitted(code: string) {
      registrationState.onCodeSubmitted(code);
    },

    goBack() {
      if (this.displayHome && this.info.backBtn) {
        window.history.back();
        return;
      }

      registrationState.goBack();
    },

    resendCode() {
      registrationState.onResendCode();
    },

    displayRegistrationError(message: string) {
      const displayMessage = message.length ? message : `${this.$t(LABEL_ERROR_OCCURED)}`;
      // https://www.npmjs.com/package/vue-toast-notification
      this.$toast.open({ message: displayMessage, type: 'error', duration: 5000, position: 'top' });
    },
  },

  computed: {
    localeUrl() {
      return this.$i18n.locale.split('_')[0]; // avoid en_gb -> en
    },

    thunderbirdNoSupported() {
      return this.thunderbirdInfo && this.thunderbirdInfo.isThunderbird === true && this.thunderbirdInfo.isSupported === false;
    },

    isThunderbird() {
      return this.thunderbirdInfo && this.thunderbirdInfo.isThunderbird;
    },

    serviceName() {
      const serviceName = this.serverInfo && this.serverInfo.ui && this.serverInfo.ui.ServiceName;
      document.title = (serviceName ? `${serviceName} - ` : '') + this.$t(LABEL_SIGN_UP).toString();
      return serviceName;
    },

    contactSupportMessage() {
      if (!this.serverInfo || !this.serverInfo.ui) {
        return null;
      }

      // const supportPageUrl = this.serverInfo.ui.supportPageUrl || '';
      /// / const url = addQueryStringParams(supportPageUrl, { src: (this.info.source || '').trim(), forcelg: this.$i18n.locale });

      // return this.$t(LABEL_PLEASECONTACT_SUPPORT_LINK, [`<a href="${url}" target="_blank">`, '</a>']);
      return this.$t(LABEL_REJECT_REASON);
    },

    displayHome() {
      return registrationState.state.panelToDisplay === PANEL.HOME;
    },

    displayRecovery() {
      return registrationState.state.panelToDisplay === PANEL.RECOVERY;
    },

    displayCaptcha() {
      return registrationState.state.panelToDisplay === PANEL.CAPTCHA;
    },

    displayCode() {
      return registrationState.state.panelToDisplay === PANEL.CODE;
    },

    displayLoader() {
      return registrationState.state.panelToDisplay === PANEL.LOADER;
    },

    displayRejected() {
      return registrationState.state.panelToDisplay === PANEL.REJECTED;
    },

    displayTrialExpired() {
      if (this.serverInfo && this.serverInfo.trialExpired === true) {
        return true;
      }

      return registrationState.state.panelToDisplay === PANEL.TRIAL_EXPIRED;
    },

    recoveryEmail() {
      return registrationState.state.recoveryEmail;
    },

    isProcessing() {
      return registrationState.state.serverLoading;
    },

    codeValidationErrors() {
      return registrationState.state.serverErrorsCode;
    },

    homeValidationErrors() {
      return registrationState.state.serverErrorsHome;
    },

    recoveryValidationErrors() {
      return registrationState.state.serverErrorsRecovery;
    },

    registrationDisabled() {
      return this.serverInfo?.disabled === true;
    },
  },

  async mounted() {
    try {
      // Registration state
      const stateHandler: StateHandler = {
        displayError: (err) => {
          this.displayRegistrationError(err);
        },

        onRegistrationDone: (data) => {
          this.onRegistrationCompleted(data);
        },
      };

      registrationState.init(stateHandler);

      // server info
      await registrationState.fetchServerInfo(this.info);
    } catch (err: any) {
      console.log('Unable to query server:', err);
      const { message } = err;
      this.displayRegistrationError(message);
    }
  },
});
</script>
